import { Event } from 'react-big-calendar';
import { GOOGLE_CALENDAR_ID, GOOGLE_CALENDAR_API_KEY, GOOGLE_CALENDAR_ICAL_URL, GOOGLE_CALENDAR_EMBED_URL } from './constants';

// Type for event resource
interface ClassEventResource {
  instructor?: string;
  level?: string;
}

// Extended Event type with typed resource
export type ClassEvent = Event & {
  resource?: ClassEventResource;
};

/**
 * Fetch events from Google Calendar using the public iCal feed
 * This works for public calendars without requiring API keys
 */
export async function fetchCalendarEventsFromICal(icalUrl: string): Promise<ClassEvent[]> {
  try {
    console.log('Fetching calendar from iCal URL:', icalUrl);
    
    // Check if URL is valid
    if (!icalUrl || !icalUrl.startsWith('http')) {
      throw new Error(`Invalid iCal URL: ${icalUrl}`);
    }
    
    // Google Calendar iCal feeds don't support CORS from browsers
    // We need to use a CORS proxy or fetch from backend
    // For development, we'll use a public CORS proxy
    // For production, recommend using Google Calendar API or backend proxy
    let fetchUrl = icalUrl;
    
    // Check if we're in browser and URL is from Google Calendar
    if (typeof window !== 'undefined' && icalUrl.includes('calendar.google.com')) {
      // Use a CORS proxy for development
      // Note: For production, use Google Calendar API or a backend proxy
      const corsProxy = 'https://api.allorigins.win/raw?url=';
      fetchUrl = corsProxy + encodeURIComponent(icalUrl);
      console.warn('Using CORS proxy for iCal feed. For production, use Google Calendar API or a backend proxy.');
    }
    
    const response = await fetch(fetchUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/calendar, text/plain, */*',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      throw new Error(`Failed to fetch calendar: ${response.statusText} (${response.status}). Response: ${errorText.substring(0, 200)}`);
    }
    
    const icalData = await response.text();
    console.log('Received iCal data, length:', icalData.length);
    
    if (!icalData || icalData.length === 0) {
      throw new Error('Received empty iCal data');
    }
    
    // Check if it looks like valid iCal data
    if (!icalData.includes('BEGIN:VCALENDAR') && !icalData.includes('BEGIN:VEVENT')) {
      console.warn('iCal data may not be in correct format. First 200 chars:', icalData.substring(0, 200));
    }
    
    // Parse iCal data using ical.js
    const ICAL = await import('ical.js');
    const jcalData = ICAL.default.parse(icalData);
    const comp = new ICAL.default.Component(jcalData);
    const vevents = comp.getAllSubcomponents('vevent');
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const events: ClassEvent[] = vevents.map((vevent: any) => {
      const event = new ICAL.default.Event(vevent);
      // toJSDate() converts iCal timezone-aware dates to JavaScript Date objects
      // JavaScript Date objects store time in UTC but display in the browser's local timezone
      // The ical.js library handles timezone conversion automatically
      const start = event.startDate.toJSDate();
      const end = event.endDate.toJSDate();
      const summary = event.summary || 'Untitled Event';
      
      // Extract instructor and level from description or summary if available
      const description = event.description || '';
      let instructor: string | undefined;
      let level: string | undefined;
      
      // Try to extract instructor/level from description
      if (description) {
        const instructorMatch = description.match(/instructor[:\s]+([^,\n]+)/i);
        if (instructorMatch) {
          instructor = instructorMatch[1].trim();
        }
        const levelMatch = description.match(/level[:\s]+([^,\n]+)/i);
        if (levelMatch) {
          level = levelMatch[1].trim();
        }
      }
      
      // Infer level from title
      if (!level) {
        const titleLower = summary.toLowerCase();
        if (titleLower.includes('fundamental') || titleLower.includes('beginner')) {
          level = 'Beginner';
        } else if (titleLower.includes('advanced')) {
          level = 'Advanced';
        } else if (titleLower.includes('teen')) {
          level = 'Teen';
        } else if (titleLower.includes('junior')) {
          level = summary.includes('1') ? 'Juniors 1' : summary.includes('2') ? 'Juniors 2' : 'Juniors';
        } else if (titleLower.includes('open mat')) {
          level = 'All Levels';
        } else {
          level = 'All Levels';
        }
      }
      
      return {
        title: summary,
        start,
        end,
        resource: {
          instructor,
          level,
        },
      };
    });
    
    console.log(`Parsed ${events.length} events from iCal feed`);
    return events;
  } catch (error) {
    console.error('Error fetching calendar events from iCal:', error);
    throw error; // Re-throw to let caller handle it
  }
}

/**
 * Fetch events from Google Calendar using the Calendar API
 * Requires API key and calendar ID
 */
export async function fetchCalendarEventsFromAPI(
  calendarId: string,
  apiKey: string,
  timeMin?: Date,
  timeMax?: Date
): Promise<ClassEvent[]> {
  try {
    console.log('Fetching calendar from Google Calendar API:', calendarId);
    const now = new Date();
    const minTime = timeMin || now;
    const maxTime = timeMax || new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000); // 60 days from now
    
    const params = new URLSearchParams({
      key: apiKey,
      timeMin: minTime.toISOString(),
      timeMax: maxTime.toISOString(),
      singleEvents: 'true',
      orderBy: 'startTime',
    });
    
    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?${params.toString()}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch calendar: ${response.statusText} (${response.status}). ${errorText}`);
    }
    
    interface GoogleCalendarEvent {
      summary?: string;
      description?: string;
      start?: {
        dateTime?: string;
        date?: string;
      };
      end?: {
        dateTime?: string;
        date?: string;
      };
    }
    
    interface GoogleCalendarResponse {
      items?: GoogleCalendarEvent[];
    }
    
    const data = await response.json() as GoogleCalendarResponse;
    
    const events: ClassEvent[] = (data.items || []).map((item: GoogleCalendarEvent) => {
      // Handle timezone-aware date parsing
      // Google Calendar API returns ISO 8601 strings with timezone info
      // new Date() automatically converts to local timezone
      let start: Date;
      let end: Date;
      
      if (item.start?.dateTime) {
        // DateTime includes timezone info - will be converted to local time
        start = new Date(item.start.dateTime);
      } else if (item.start?.date) {
        // All-day events (date only, no time) - treat as local date at midnight
        start = new Date(item.start.date + 'T00:00:00');
      } else {
        start = new Date();
      }
      
      if (item.end?.dateTime) {
        end = new Date(item.end.dateTime);
      } else if (item.end?.date) {
        // All-day events end at start of next day
        end = new Date(item.end.date + 'T00:00:00');
      } else {
        end = new Date(start.getTime() + 60 * 60 * 1000); // Default 1 hour
      }
      
      const summary = item.summary || 'Untitled Event';
      const description = item.description || '';
      
      // Extract instructor and level from description
      let instructor: string | undefined;
      let level: string | undefined;
      
      if (description) {
        const instructorMatch = description.match(/instructor[:\s]+([^,\n]+)/i);
        if (instructorMatch) {
          instructor = instructorMatch[1].trim();
        }
        const levelMatch = description.match(/level[:\s]+([^,\n]+)/i);
        if (levelMatch) {
          level = levelMatch[1].trim();
        }
      }
      
      // Infer level from title
      if (!level) {
        const titleLower = summary.toLowerCase();
        if (titleLower.includes('fundamental') || titleLower.includes('beginner')) {
          level = 'Beginner';
        } else if (titleLower.includes('advanced')) {
          level = 'Advanced';
        } else if (titleLower.includes('teen')) {
          level = 'Teen';
        } else if (titleLower.includes('junior')) {
          level = summary.includes('1') ? 'Juniors 1' : summary.includes('2') ? 'Juniors 2' : 'Juniors';
        } else if (titleLower.includes('open mat')) {
          level = 'All Levels';
        } else {
          level = 'All Levels';
        }
      }
      
      return {
        title: summary,
        start,
        end,
        resource: {
          instructor,
          level,
        },
      };
    });
    
    console.log(`Parsed ${events.length} events from Google Calendar API`);
    return events;
  } catch (error) {
    console.error('Error fetching calendar events from API:', error);
    throw error; // Re-throw to let caller handle it
  }
}

/**
 * Main function to fetch calendar events
 * Tries API first, then falls back to iCal if API is not configured
 */
export async function fetchCalendarEvents(
  icalUrl?: string,
  timeMin?: Date,
  timeMax?: Date
): Promise<ClassEvent[]> {
  console.log('fetchCalendarEvents called with:', { icalUrl, hasApiKey: !!GOOGLE_CALENDAR_API_KEY, hasCalendarId: !!GOOGLE_CALENDAR_ID, hasICalUrl: !!GOOGLE_CALENDAR_ICAL_URL });
  
  // If API key and calendar ID are configured, use API
  if (GOOGLE_CALENDAR_API_KEY && GOOGLE_CALENDAR_ID) {
    console.log('Using Google Calendar API');
    return fetchCalendarEventsFromAPI(GOOGLE_CALENDAR_ID, GOOGLE_CALENDAR_API_KEY, timeMin, timeMax);
  }
  
  // Try provided iCal URL first, then fall back to env var
  const urlToUse = icalUrl || GOOGLE_CALENDAR_ICAL_URL;
  console.log('iCal URL to use:', urlToUse || 'NONE');
  
  if (urlToUse) {
    console.log('Using iCal feed');
    return fetchCalendarEventsFromICal(urlToUse);
  }
  
  // If no configuration, return empty array
  console.warn('No Google Calendar configuration found. Please set one of:');
  console.warn('  - VITE_GOOGLE_CALENDAR_ID + VITE_GOOGLE_CALENDAR_API_KEY (RECOMMENDED - Google Calendar API, supports CORS)');
  console.warn('  - VITE_GOOGLE_CALENDAR_ICAL_URL (uses CORS proxy - works but not ideal for production)');
  console.warn('  - VITE_GOOGLE_CALENDAR_EMBED_URL (will extract calendar ID from embed URL)');
  console.warn('');
  console.warn('NOTE: Google Calendar iCal feeds have CORS restrictions. For production, use the Google Calendar API.');
  console.warn('Current env values:', {
    GOOGLE_CALENDAR_ICAL_URL: GOOGLE_CALENDAR_ICAL_URL || 'NOT SET',
    GOOGLE_CALENDAR_ID: GOOGLE_CALENDAR_ID || 'NOT SET',
    GOOGLE_CALENDAR_API_KEY: GOOGLE_CALENDAR_API_KEY ? 'SET (hidden)' : 'NOT SET',
    GOOGLE_CALENDAR_EMBED_URL: GOOGLE_CALENDAR_EMBED_URL || 'NOT SET',
  });
  return [];
}

